import { useEffect, useState } from "react";
import {
    AlertTriangle,
    Calendar,
    Coins,
    FileWarning,
    Landmark,
    Percent,
    Plus,
    Receipt,
    RefreshCw,
    Sparkles,
    TrendingDown,
    Wallet,
    Trash2,
    Check,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// CockpitAdmin — ADMINISTRATIF & DÉCLARATIONS
// Vue globale + Calendrier + Déclarations + Alertes
// ─────────────────────────────────────────────────────────────

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
});
const fmt = (n) => EUR.format(Number(n) || 0);

const DEADLINE_TYPES = [
    { v: "tva", l: "TVA" },
    { v: "urssaf", l: "URSSAF" },
    { v: "is", l: "IS" },
    { v: "cfe", l: "CFE" },
    { v: "annual", l: "Liasse fiscale" },
    { v: "deposit", l: "Dépôt des comptes" },
    { v: "custom", l: "Personnalisée" },
];

const URGENCY_COLOR = {
    green: "text-[#a3d39c] border-[#a3d39c]/40",
    orange: "text-[#e9c878] border-[#e9c878]/40",
    red: "text-[#e89090] border-[#e89090]/50",
};

export default function CockpitAdmin() {
    const [overview, setOverview] = useState(null);
    const [calendar, setCalendar] = useState([]);
    const [declarations, setDeclarations] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const year = new Date().getFullYear();

    const load = async () => {
        setBusy(true);
        setErr("");
        try {
            const [o, c, d, a] = await Promise.all([
                cockpit.get("/admin/overview").then((r) => r.data),
                cockpit.get(`/admin/calendar?year=${year}`).then((r) => r.data),
                cockpit.get(`/admin/declarations?year=${year}`).then((r) => r.data),
                cockpit.get("/admin/alerts").then((r) => r.data),
            ]);
            setOverview(o);
            setCalendar(c.items || []);
            setDeclarations(d);
            setAlerts(a.items || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Chargement impossible");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const seedYear = async () => {
        setBusy(true);
        try {
            const { data } = await cockpit.post(
                `/admin/calendar/seed-year?year=${year}`,
            );
            if (data.created === 0) {
                alert(
                    "Le calendrier est déjà initialisé pour cette année.",
                );
            }
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'initialisation");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="cockpit-admin"
            className="max-w-[1400px] mx-auto px-6 md:px-12 py-12"
        >
            {/* HEADER */}
            <div className="mb-12 flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                        <FileWarning strokeWidth={1.1} size={11} />
                        Cockpit administratif · confidentiel
                    </div>
                    <h1
                        className="font-serif text-[32px] sm:text-[40px] lg:text-[48px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 300 }}
                    >
                        Pilotage administratif & déclarations.
                    </h1>
                    <p className="mt-3 text-[13px] sm:text-[14px] text-[#f4ecd8]/55 italic font-light max-w-2xl">
                        Vue consolidée des obligations fiscales, des
                        provisions automatiques et de la trésorerie réellement
                        disponible.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="admin-refresh"
                    onClick={load}
                    disabled={busy}
                    className="inline-flex items-center gap-3 px-5 py-2.5 border border-white/20 text-[#f4ecd8] text-[11px] tracking-[0.3em] uppercase hover:bg-white/[0.04] transition-all duration-500 disabled:opacity-40"
                >
                    <RefreshCw strokeWidth={1.2} size={13} className={busy ? "animate-spin" : ""} />
                    Rafraîchir
                </button>
            </div>

            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1 mb-6">
                    {err}
                </div>
            )}

            {/* 1. VUE GLOBALE */}
            {overview && <OverviewSection overview={overview} />}

            {/* 6. ALERTES */}
            <AlertsSection alerts={alerts} />

            {/* 2. CALENDRIER */}
            <CalendarSection
                items={calendar}
                onSeedYear={seedYear}
                onReload={load}
                busy={busy}
                year={year}
            />

            {/* 5. DÉCLARATIONS */}
            {declarations && <DeclarationsSection data={declarations} />}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// OVERVIEW — Vue globale (10 KPIs)
// ─────────────────────────────────────────────────────────────
function OverviewSection({ overview }) {
    const items = [
        { key: "vat_collected", label: "TVA collectée", value: overview.vat_collected, Icon: Receipt },
        { key: "vat_deductible", label: "TVA déductible", value: overview.vat_deductible, Icon: Receipt, muted: true },
        { key: "vat_net_due", label: "TVA à reverser", value: overview.vat_net_due, Icon: Landmark, accent: true },
        { key: "is_estimated", label: "IS estimé", value: overview.is_estimated, Icon: Landmark, accent: true },
        { key: "treasury_gross", label: "Trésorerie brute", value: overview.treasury_gross, Icon: Wallet, muted: true },
        { key: "treasury_available", label: "Trésorerie disponible", value: overview.treasury_available, Icon: Wallet, accent: true },
        { key: "safety_reserve", label: "Réserve de sécurité", value: overview.safety_reserve, Icon: Percent, muted: true },
        { key: "remaining_to_invoice", label: "Reste à facturer", value: overview.remaining_to_invoice, Icon: TrendingDown, muted: true },
        { key: "revenue_paid_ht", label: "Honoraires encaissés", value: overview.revenue_paid_ht, Icon: Coins },
        { key: "success_fees_collected", label: "Success fees encaissés", value: overview.success_fees_collected, Icon: Sparkles, muted: true },
    ];
    return (
        <section
            data-testid="admin-overview"
            className="mb-14"
        >
            <div className="overline text-[#d4af37]/70 mb-5">
                Vue globale
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/[0.05]">
                {items.map(({ key, label, value, Icon, accent, muted }) => (
                    <div
                        key={key}
                        data-testid={`admin-kpi-${key}`}
                        className="bg-[#070d18] p-6 lg:p-7"
                    >
                        <div className="flex items-center gap-2 mb-3 text-[#d4af37]/55">
                            <Icon strokeWidth={1.1} size={13} />
                            <span className="text-[9.5px] tracking-[0.22em] uppercase">
                                {label}
                            </span>
                        </div>
                        <div
                            className={`font-serif text-[20px] sm:text-[22px] leading-none tabular-nums ${
                                accent
                                    ? "text-[#d4af37]"
                                    : muted
                                      ? "text-[#f4ecd8]/65"
                                      : "text-[#f4ecd8]"
                            }`}
                            style={{ fontWeight: 400 }}
                        >
                            {fmt(value)}
                        </div>
                    </div>
                ))}
            </div>
            {/* Formula reminder */}
            <p className="mt-5 text-[12px] text-[#f4ecd8]/40 italic font-light leading-relaxed border-l-2 border-[#d4af37]/30 pl-4">
                Trésorerie disponible = Trésorerie brute − TVA à reverser
                ({overview.settings.vat_deductible_pct}% déductible) − IS
                estimé ({overview.settings.is_rate}%) − Réserve de sécurité
                ({overview.settings.safety_reserve_pct}%).
            </p>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// ALERTS — Centre d'alertes
// ─────────────────────────────────────────────────────────────
function AlertsSection({ alerts }) {
    return (
        <section data-testid="admin-alerts" className="mb-14">
            <div className="flex items-baseline justify-between gap-4 mb-5 flex-wrap">
                <div className="overline text-[#d4af37]/70 flex items-center gap-2">
                    <AlertTriangle strokeWidth={1.1} size={11} />
                    Centre d'alertes
                    <span className="ml-2 text-[10px] text-[#f4ecd8]/45 normal-case tracking-normal">
                        ({alerts.length})
                    </span>
                </div>
            </div>
            {alerts.length === 0 ? (
                <p className="text-[13px] text-[#f4ecd8]/45 italic font-light">
                    Aucune alerte active — tout est sous contrôle.
                </p>
            ) : (
                <ul className="border border-white/[0.06] divide-y divide-white/[0.05]">
                    {alerts.map((a, i) => (
                        <li
                            key={i}
                            data-testid={`admin-alert-${i}`}
                            className="flex items-center gap-4 px-5 py-3.5"
                        >
                            <span
                                className={`shrink-0 w-2 h-2 rounded-full ${
                                    a.severity === "red"
                                        ? "bg-[#e89090]"
                                        : a.severity === "orange"
                                          ? "bg-[#e9c878]"
                                          : "bg-[#a3d39c]"
                                }`}
                            />
                            <span className="font-serif text-[14px] text-[#f4ecd8]/90 flex-1 italic">
                                {a.message}
                            </span>
                            {a.amount > 0 && (
                                <span className="font-serif text-[13px] text-[#d4af37] tabular-nums shrink-0">
                                    {fmt(a.amount)}
                                </span>
                            )}
                            <span
                                className={`text-[9.5px] tracking-[0.22em] uppercase shrink-0 ${
                                    a.severity === "red"
                                        ? "text-[#e89090]"
                                        : a.severity === "orange"
                                          ? "text-[#e9c878]"
                                          : "text-[#a3d39c]"
                                }`}
                            >
                                {a.kind.replace(/_/g, " ")}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// CALENDAR — Calendrier administratif + CRUD
// ─────────────────────────────────────────────────────────────
function CalendarSection({ items, onSeedYear, onReload, busy, year }) {
    const [adding, setAdding] = useState(false);
    return (
        <section data-testid="admin-calendar" className="mb-14">
            <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
                <div className="overline text-[#d4af37]/70 flex items-center gap-2">
                    <Calendar strokeWidth={1.1} size={11} />
                    Calendrier administratif · {year}
                    <span className="ml-2 text-[10px] text-[#f4ecd8]/45 normal-case tracking-normal">
                        ({items.length})
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="admin-seed-year"
                        onClick={onSeedYear}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-white/20 text-[#f4ecd8]/75 hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-500"
                    >
                        Initialiser année
                    </button>
                    <button
                        type="button"
                        data-testid="admin-add-deadline"
                        onClick={() => setAdding((v) => !v)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Plus strokeWidth={1.2} size={12} />
                        Ajouter
                    </button>
                </div>
            </div>

            {adding && (
                <DeadlineForm
                    onClose={() => setAdding(false)}
                    onCreated={() => {
                        setAdding(false);
                        onReload();
                    }}
                />
            )}

            {items.length === 0 ? (
                <p className="text-[13px] text-[#f4ecd8]/45 italic font-light">
                    Aucune échéance pour {year}. Cliquez sur « Initialiser
                    année » pour générer les obligations standards.
                </p>
            ) : (
                <ul className="border border-white/[0.06] divide-y divide-white/[0.04]">
                    {items.map((it) => (
                        <DeadlineRow key={it.id} item={it} onReload={onReload} />
                    ))}
                </ul>
            )}
        </section>
    );
}

function DeadlineRow({ item, onReload }) {
    const [busy, setBusy] = useState(false);
    const markPaid = async () => {
        setBusy(true);
        try {
            await cockpit.put(`/admin/calendar/${item.id}`, {
                status: item.status === "paid" ? "pending" : "paid",
            });
            onReload();
        } finally {
            setBusy(false);
        }
    };
    const remove = async () => {
        if (!window.confirm(`Supprimer « ${item.label} » ?`)) return;
        setBusy(true);
        try {
            await cockpit.delete(`/admin/calendar/${item.id}`);
            onReload();
        } finally {
            setBusy(false);
        }
    };
    const date = new Date(item.due_date).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
    });
    const paid = item.status === "paid";
    return (
        <li
            data-testid={`admin-deadline-${item.id}`}
            className={`grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-5 py-3.5 ${
                paid ? "opacity-50" : ""
            }`}
        >
            <span
                className={`shrink-0 w-2 h-2 rounded-full ${
                    paid
                        ? "bg-[#a3d39c]"
                        : item.urgency === "red"
                          ? "bg-[#e89090]"
                          : item.urgency === "orange"
                            ? "bg-[#e9c878]"
                            : "bg-[#a3d39c]"
                }`}
            />
            <div className="min-w-0">
                <div
                    className={`font-serif text-[14.5px] truncate ${paid ? "line-through text-[#f4ecd8]/55" : "text-[#f4ecd8]"}`}
                    style={{ fontWeight: 400 }}
                >
                    {item.label}
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mt-0.5">
                    {item.type} · {date}
                </div>
            </div>
            <span
                className={`text-[10.5px] tracking-[0.22em] uppercase tabular-nums shrink-0 ${URGENCY_COLOR[item.urgency] || "text-[#f4ecd8]/55"}`}
            >
                {paid
                    ? "Réglé"
                    : item.days_remaining < 0
                      ? `Retard ${Math.abs(item.days_remaining)}j`
                      : `J-${item.days_remaining}`}
            </span>
            {item.amount_due > 0 && (
                <span className="font-serif text-[14px] text-[#d4af37] tabular-nums shrink-0">
                    {fmt(item.amount_due)}
                </span>
            )}
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    data-testid={`admin-deadline-toggle-${item.id}`}
                    onClick={markPaid}
                    disabled={busy}
                    title={paid ? "Marquer en attente" : "Marquer réglé"}
                    className="text-[#f4ecd8]/45 hover:text-[#a3d39c] transition-colors duration-500"
                >
                    <Check strokeWidth={1.2} size={14} />
                </button>
                <button
                    type="button"
                    data-testid={`admin-deadline-delete-${item.id}`}
                    onClick={remove}
                    disabled={busy}
                    className="text-[#f4ecd8]/35 hover:text-[#e89090] transition-colors duration-500"
                >
                    <Trash2 strokeWidth={1.2} size={13} />
                </button>
            </div>
        </li>
    );
}

function DeadlineForm({ onCreated, onClose }) {
    const [form, setForm] = useState({
        type: "custom",
        label: "",
        due_date: "",
        period: "",
        amount_due: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.post("/admin/calendar", {
                ...form,
                amount_due: Number(form.amount_due) || 0,
            });
            onCreated();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            data-testid="admin-deadline-form"
            onSubmit={submit}
            className="mb-5 bg-[#070d18] border border-[#d4af37]/25 p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
            <Select
                label="Type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                options={DEADLINE_TYPES}
            />
            <Field
                label="Libellé"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="ex : Acompte IS Q2"
                required
            />
            <Field
                label="Date limite"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                required
            />
            <Field
                label="Période"
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                placeholder="ex : 2026-Q2"
            />
            <Field
                label="Montant prévisionnel (€)"
                type="number"
                step="100"
                value={form.amount_due}
                onChange={(e) => setForm({ ...form, amount_due: e.target.value })}
            />
            <Field
                label="Note"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="optionnel"
            />
            {err && (
                <div className="sm:col-span-2 lg:col-span-3 text-[12.5px] text-[#e89090] border-l-2 border-[#e89090] pl-3">
                    {err}
                </div>
            )}
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-deadline-submit"
                    className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.28em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    {busy ? "Enregistrement…" : "Ajouter l'échéance"}
                </button>
            </div>
        </form>
    );
}

// ─────────────────────────────────────────────────────────────
// DECLARATIONS — TVA quarterly + IS/URSSAF/CFE annuels
// ─────────────────────────────────────────────────────────────
function DeclarationsSection({ data }) {
    const MONTHS_FR = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];
    return (
        <section data-testid="admin-declarations" className="mb-10">
            <div className="overline text-[#d4af37]/70 mb-5 flex items-center gap-2">
                <Receipt strokeWidth={1.1} size={11} />
                Déclarations · {data.year}
            </div>

            {/* TVA quarterly */}
            <div className="mb-8">
                <div className="overline text-[#d4af37]/55 mb-3">
                    TVA · synthèse trimestrielle
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.05]">
                    {data.quarterly.map((q) => (
                        <div
                            key={q.quarter}
                            data-testid={`admin-decl-q${q.quarter}`}
                            className="bg-[#070d18] p-5"
                        >
                            <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                                Q{q.quarter} {data.year}
                            </div>
                            <div className="space-y-1">
                                <KVLine k="Collectée" v={fmt(q.vat_collected)} />
                                <KVLine k="Déductible" v={fmt(q.vat_deductible)} muted />
                                <KVLine k="Nette" v={fmt(q.vat_net)} accent />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* IS / URSSAF / CFE annual */}
            <div className="mb-8">
                <div className="overline text-[#d4af37]/55 mb-3">
                    Annuel
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.05]">
                    <div className="bg-[#070d18] p-5">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                            Honoraires encaissés
                        </div>
                        <div className="font-serif text-[20px] text-[#f4ecd8] tabular-nums">
                            {fmt(data.annual.revenue_paid)}
                        </div>
                    </div>
                    <div className="bg-[#070d18] p-5">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                            IS estimé · {data.rates.is_rate}%
                        </div>
                        <div className="font-serif text-[20px] text-[#d4af37] tabular-nums">
                            {fmt(data.annual.is_estimated)}
                        </div>
                    </div>
                    <div className="bg-[#070d18] p-5">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                            URSSAF estimé · {data.rates.urssaf_pct}%
                        </div>
                        <div className="font-serif text-[20px] text-[#f4ecd8]/85 tabular-nums">
                            {fmt(data.annual.urssaf_estimated)}
                        </div>
                    </div>
                    <div className="bg-[#070d18] p-5">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                            TVA nette annuelle
                        </div>
                        <div className="font-serif text-[20px] text-[#f4ecd8] tabular-nums">
                            {fmt(data.annual.vat_net)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Monthly detail collapsible */}
            <details>
                <summary className="cursor-pointer text-[10.5px] tracking-[0.28em] uppercase text-[#d4af37]/65 hover:text-[#d4af37] mb-3 select-none">
                    Détail mensuel TVA
                </summary>
                <ul className="border border-white/[0.06] divide-y divide-white/[0.04] mt-3">
                    {data.monthly.map((m) => (
                        <li
                            key={m.month}
                            className="grid grid-cols-[100px_1fr_1fr_1fr_1fr] gap-3 items-center px-5 py-2.5 text-[13px] font-serif"
                        >
                            <span className="overline text-[#d4af37]/65">
                                {MONTHS_FR[m.month - 1]}
                            </span>
                            <span className="text-[#f4ecd8]/85 tabular-nums">
                                Encaissé : {fmt(m.revenue_paid)}
                            </span>
                            <span className="text-[#f4ecd8] tabular-nums">
                                TVA col. : {fmt(m.vat_collected)}
                            </span>
                            <span className="text-[#f4ecd8]/55 tabular-nums">
                                Déduct. : {fmt(m.vat_deductible)}
                            </span>
                            <span className="text-[#d4af37] tabular-nums">
                                Nette : {fmt(m.vat_net)}
                            </span>
                        </li>
                    ))}
                </ul>
            </details>
        </section>
    );
}

function KVLine({ k, v, accent, muted }) {
    return (
        <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] tracking-[0.18em] uppercase text-[#f4ecd8]/45">
                {k}
            </span>
            <span
                className={`font-serif text-[15px] tabular-nums ${
                    accent
                        ? "text-[#d4af37]"
                        : muted
                          ? "text-[#f4ecd8]/55"
                          : "text-[#f4ecd8]"
                }`}
            >
                {v}
            </span>
        </div>
    );
}

function Field({ label, ...props }) {
    return (
        <div>
            <label className="block text-[9.5px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-1.5">
                {label}
            </label>
            <input
                {...props}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
            />
        </div>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <div>
            <label className="block text-[9.5px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-1.5">
                {label}
            </label>
            <select
                value={value}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v} className="bg-[#0b1220]">
                        {o.l}
                    </option>
                ))}
            </select>
        </div>
    );
}
